/**
 * Notifications Hook
 * Manages Firebase FCM integration: permission handling, token registration,
 * and real-time foreground/background message handling.
 */

import { useEffect, useCallback, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { App } from "antd";
import {
  useRegisterGuestFCMTokenMutation,
  useRemoveGuestFCMTokenMutation,
} from "../store/api/notificationsApi";
import { apiSlice } from "../store/api/apiSlice";
import { selectAuth } from "../store/slices/authSlice";
import {
  isNotificationSupported,
  getPermissionStatus,
  requestNotificationPermission,
  getRegistrationToken,
  storeToken,
  clearStoredToken,
  getStoredToken,
  onForegroundMessage,
  isIOS,
  isInstalledPWA,
} from "../utils/browserNotifications";

// Module-level flag: only the FIRST hook instance sets up the foreground listener.
let foregroundListenerOwner = null;

export const useNotifications = () => {
  const { sessionId } = useSelector(selectAuth);
  const dispatch = useDispatch();
  const appContext = App.useApp();
  const messageApi = appContext.message;

  const [registerTokenMutation] = useRegisterGuestFCMTokenMutation();
  const [removeTokenMutation] = useRemoveGuestFCMTokenMutation();

  const [permissionStatus, setPermissionStatus] = useState(getPermissionStatus());
  const [isRegistered, setIsRegistered] = useState(() => {
    const storedToken = getStoredToken();
    const storedSession = localStorage.getItem("fcmTokenSession");
    const hasValidSession = sessionId ? storedSession === sessionId : true;
    return !!(storedToken && hasValidSession);
  });
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  const unsubscribeRef = useRef(null);

  const isSupported = isNotificationSupported();
  const isMobileIOSBrowser = isIOS() && !isInstalledPWA();
  const isEnabled = isRegistered;

  /**
   * Request notification permission and register FCM token
   */
  const requestPermission = useCallback(async () => {
    if (!sessionId) {
      messageApi.warning("Session required to enable notifications");
      return false;
    }

    if (isIOS() && !isInstalledPWA()) {
      messageApi.warning(
        "On iPhone/iPad, please add this app to your Home Screen first, then enable notifications.",
        6,
      );
      return false;
    }

    try {
      const result = await requestNotificationPermission();

      if (!result.granted) {
        setPermissionStatus(result.permission);
        messageApi.warning(result.error || "Failed to enable notifications");
        return false;
      }

      setPermissionStatus("granted");

      const token = await getRegistrationToken();
      if (!token) {
        const hasPushManager = "PushManager" in window;
        const isSecure = location.protocol === "https:" || location.hostname === "localhost";

        console.error("[Notifications] Token fetch failed.", {
          userAgent: navigator.userAgent,
          hasPushManager,
          isSecure,
        });

        if (!hasPushManager) {
          messageApi.error(
            "Your browser does not support web push. Safari requires macOS 13 (Ventura) or later. Try Chrome or Firefox instead.",
            8,
          );
        } else if (!isSecure) {
          messageApi.error("Push notifications require HTTPS.", 6);
        } else {
          messageApi.error("Could not complete notification setup. Please try Chrome or Firefox.", 6);
        }
        return false;
      }

      storeToken(token, sessionId);

      const deviceId =
        localStorage.getItem("deviceId") ||
        `web-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
      localStorage.setItem("deviceId", deviceId);

      await registerTokenMutation({
        token,
        deviceId,
        platform: "web",
        appVersion: import.meta.env.VITE_APP_VERSION || "1.0.0",
        deviceInfo: {
          model: navigator.userAgent,
          osVersion: navigator.platform,
          appBuild: import.meta.env.VITE_APP_VERSION || "1.0.0",
        },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      setIsRegistered(true);
      window.dispatchEvent(new CustomEvent("fcm-registration-change", { detail: { registered: true } }));
      messageApi.success("Notifications enabled successfully!");
      return true;
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Unknown error");
      setError(error);
      messageApi.error(error.message || "Failed to enable notifications");
      return false;
    }
  }, [sessionId, messageApi, registerTokenMutation]);

  /**
   * Unregister from notifications
   */
  const unregisterToken = useCallback(async () => {
    const storedToken = getStoredToken();
    try {
      if (storedToken) {
        await removeTokenMutation(storedToken);
      }
      clearStoredToken();
      setIsRegistered(false);
      window.dispatchEvent(new CustomEvent("fcm-registration-change", { detail: { registered: false } }));
      messageApi.info("Notifications disabled");
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Unknown error");
      setError(error);
      messageApi.error(error.message || "Failed to disable notifications");
    }
  }, [removeTokenMutation, messageApi]);

  /**
   * Unified toggle
   */
  const toggleNotifications = useCallback(
    async (checked) => {
      if (checked) {
        return await requestPermission();
      } else {
        await unregisterToken();
        return true;
      }
    },
    [requestPermission, unregisterToken],
  );

  /**
   * Handle incoming FCM messages when app is in foreground
   */
  const handleForegroundMessage = useCallback(
    (payload) => {
      console.log("Foreground message received:", payload);

      const notificationTitle = payload.notification?.title || payload.data?.title || "New Update";
      const notificationBody = payload.notification?.body || payload.data?.body || "";

      // Instantly invalidate RTK Query cache so pages refetch without polling
      dispatch(apiSlice.util.invalidateTags([{ type: "Request", id: "LIST" }, "Notifications"]));

      if (payload.data?.requestId) {
        dispatch(apiSlice.util.invalidateTags([{ type: "Request", id: payload.data.requestId }]));
      }

      // Play notification sound
      try {
        const audio = new Audio("/notification-sound.mp3");
        audio.volume = 0.8;
        audio.play().catch(() => {});
      } catch (e) {
        console.warn("Could not play notification sound:", e);
      }

      // Show Ant Design in-app message (user is already looking at the app)
      messageApi.info({
        content: (
          <div>
            <strong>{notificationTitle}</strong>
            <br />
            {notificationBody}
          </div>
        ),
        duration: 5,
        onClick: () => {
          if (payload.data?.url) {
            window.location.href = payload.data.url;
          }
        },
      });
    },
    [dispatch, messageApi],
  );

  // Firebase foreground subscription — only ONE instance should own this.
  const instanceId = useRef(Symbol("notif-hook"));

  useEffect(() => {
    // Captured once here rather than read inside the cleanup. The ref holds a
    // stable per-instance Symbol, but reading .current from a cleanup closure is
    // the pattern react-hooks warns about, and capturing makes the intent explicit.
    const myInstanceId = instanceId.current;

    if (foregroundListenerOwner && foregroundListenerOwner !== myInstanceId) {
      return;
    }

    if (unsubscribeRef.current && typeof unsubscribeRef.current === "function") {
      try {
        unsubscribeRef.current();
      } catch (e) {
        // iOS can throw NotFoundError when cleaning up stale listeners
        console.warn("[Notifications] Cleanup error (iOS idle?):", e.message);
      }
      unsubscribeRef.current = null;
    }

    if (isRegistered) {
      foregroundListenerOwner = myInstanceId;
      try {
        unsubscribeRef.current = onForegroundMessage(handleForegroundMessage);
      } catch (e) {
        // Don't crash if Firebase messaging is unavailable
        console.warn("[Notifications] Could not set up foreground listener:", e.message);
      }
    }

    return () => {
      if (foregroundListenerOwner === myInstanceId) {
        if (unsubscribeRef.current && typeof unsubscribeRef.current === "function") {
          try {
            unsubscribeRef.current();
          } catch (_e) {
            // Ignore cleanup errors
          }
          unsubscribeRef.current = null;
        }
        foregroundListenerOwner = null;
      }
    };
  }, [isRegistered, handleForegroundMessage]);

  // Listen for service worker messages (background FCM notifications)
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      try {
        if (event.data?.type === "FCM_BACKGROUND_MESSAGE") {
          console.log("[Notifications] Background FCM message received, invalidating cache");
          dispatch(apiSlice.util.invalidateTags([{ type: "Request", id: "LIST" }, "Notifications"]));
        }

        if (event.data?.type === "NOTIFICATION_CLICK" && event.data?.url) {
          dispatch(apiSlice.util.invalidateTags([{ type: "Request", id: "LIST" }, "Notifications"]));
          if (window.location.pathname !== event.data.url.split("?")[0]) {
            window.location.href = event.data.url;
          }
        }
      } catch (e) {
        console.warn("[Notifications] Error handling SW message:", e.message);
      }
    };

    try {
      navigator.serviceWorker?.addEventListener("message", handleServiceWorkerMessage);
    } catch (e) {
      // iOS can throw if SW is unavailable
      console.warn("[Notifications] Could not add SW message listener:", e.message);
    }
    
    return () => {
      try {
        navigator.serviceWorker?.removeEventListener("message", handleServiceWorkerMessage);
      } catch (e) {
        // Ignore cleanup errors
      }
    };
  }, [dispatch]);

  // Permission state sync
  useEffect(() => {
    if ("permissions" in navigator) {
      navigator.permissions
        .query({ name: "notifications" })
        .then((permission) => {
          const handler = () => {
            if (mountedRef.current) setPermissionStatus(getPermissionStatus());
          };
          permission.addEventListener("change", handler);
          return () => permission.removeEventListener("change", handler);
        })
        .catch(() => {});
    }

    return () => { mountedRef.current = false; };
  }, []);

  // Token registration sync
  useEffect(() => {
    if (!sessionId) return;
    const storedToken = getStoredToken();
    const storedSession = localStorage.getItem("fcmTokenSession");
    if (storedToken && storedSession === sessionId) {
      setIsRegistered(true);
    }
  }, [sessionId]);

  // Auto-register if browser permission already granted but token not registered for this session
  useEffect(() => {
    if (!sessionId) return;
    if (!isSupported) return;
    
    // Check if permission is already granted
    if (getPermissionStatus() !== 'granted') return;
    
    // Check if already registered for this session
    const storedToken = getStoredToken();
    const storedSession = localStorage.getItem("fcmTokenSession");
    if (storedToken && storedSession === sessionId) return;
    
    // Check if user manually disabled notifications
    if (localStorage.getItem('notificationsManuallyDisabled') === 'true') return;

    // Auto-register silently
    const autoRegister = async () => {
      try {
        const token = await getRegistrationToken();
        if (!token) return;

        storeToken(token, sessionId);

        const deviceId =
          localStorage.getItem("deviceId") ||
          `web-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
        localStorage.setItem("deviceId", deviceId);

        await registerTokenMutation({
          token,
          deviceId,
          platform: "web",
          appVersion: import.meta.env.VITE_APP_VERSION || "1.0.0",
          deviceInfo: {
            model: navigator.userAgent,
            osVersion: navigator.platform,
            appBuild: import.meta.env.VITE_APP_VERSION || "1.0.0",
          },
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });

        setIsRegistered(true);
        window.dispatchEvent(new CustomEvent("fcm-registration-change", { detail: { registered: true } }));
        console.log("[Notifications] Auto-registered for new session");
      } catch (err) {
        console.warn("[Notifications] Auto-registration failed:", err);
      }
    };

    autoRegister();
  }, [sessionId, isSupported, registerTokenMutation]);

  // Sync isRegistered across all hook instances via custom event
  useEffect(() => {
    const handler = (e) => {
      setIsRegistered(e.detail.registered);
    };
    window.addEventListener("fcm-registration-change", handler);
    return () => window.removeEventListener("fcm-registration-change", handler);
  }, []);

  return {
    isSupported,
    isMobileIOSBrowser,
    permissionStatus,
    isRegistered,
    isEnabled,
    error,
    requestPermission,
    toggleNotifications,
  };
};
